import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Calendar, Shield, ShieldOff, Key, UserCog, Edit, Users, Eye, EyeOff, Check, ChevronsUpDown, X as ClearIcon, Ban, Linkedin, CaseSensitive } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { motion, AnimatePresence } from 'framer-motion';
import { updateUserDetails, changeUserRole, changeUserPassword, updateUserBlockStatus } from '../../services/usersapi';
import { toast } from 'react-hot-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from '../lib/utils';
import { getAssignedManagers } from '../../services/apiclient';
import { useRbac } from '../../context';
import { UnblockRequestsModal } from '../UnblockRequestsModal';
import { getUnblockRequestsForReview } from '../../services/unblockRequestApi';


interface User {
  id: string;
  username: string;
  email: string;
  phone: string;
  role: string;
  location: string;
  isBlocked: boolean;
  createdAt: string;
  profilePhotoUrl?: string;
  linkedinUrl?: string;
  currentManager?: {
    id: string;
    username: string;
    email: string;
    phone: string;
  };
  roleHistory?: Array<{
    role: string;
    startDate: string;
    endDate: string | null;
    manager: {
      id: string;
      username: string;
      email: string;
      phone: string;
    };
  }>;
  blockHistory?: Array<{
    isBlocked: boolean;
    changedAt: string;
    changedBy?: {
      id: string;
      username: string;
      email: string;
      role: string;
    } | null;
  }>;
}

interface UserDetailSidebarProps {
  user: User | null;
  onClose: () => void;
  onUserUpdate?: (updatedUser: User) => void;
  setUserEdited: (edited: boolean) => void;
  
}

interface EditUserData {
  username: string;
  location: string;
  phone: string;
}

interface Manager {
  id: string;
  username: string;
  email: string;
  phone: string;
  role?: string;
}

const roles = ['ADMIN', 'SHM', 'HM', 'AGENCY', 'HR', 'FBDE', 'CONSULTANT', 'BDE', 'FREELANCE_COUNSELLOR', 'TPO'];

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Admin',
};

const sidebarVariants = {
  hidden: { x: '100%', opacity: 0 },
  visible: { x: 0, opacity: 1 },
  exit: { x: '100%', opacity: 0 },
};

const UserDetailSidebar: React.FC<UserDetailSidebarProps> = ({ user, onClose, onUserUpdate, setUserEdited }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editData, setEditData] = useState<EditUserData>({
    username: user?.username || '',
    location: user?.location || '',
    phone: user?.phone || '',
  });
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user?.role || '');
  const [selectedManagerUserId, setSelectedManagerUserId] = useState('');
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [managerSearch, setManagerSearch] = useState(""); // State for manager search
  const [showUnblockRequestsModal, setShowUnblockRequestsModal] = useState(false);
  const [managerPopoverOpen, setManagerPopoverOpen] = useState(false); // State for manager popover
  const [pendingUnblockCount, setPendingUnblockCount] = useState(0);
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);

  const getApiRoleParam = (role: string): string | string[] => {
    const normalizedRole = role === 'Admin' ? 'ADMIN' : role;
    switch (normalizedRole) {
      case 'Master BDE':
      case 'BDE':
      case 'CONSULTANT':
      case 'SHM_ADMIN':
      case 'AGENCY_ADMIN':
      case 'FBDE_ADMIN':
      case 'FREELANCE_COUNSELLOR':
      case 'ADMIN':
        return 'ADMIN';
      case 'HM':
        return ['SHM','FBDE','AGENCY'];
      case 'AGENCY':
        return ['AGENCY_ADMIN'];
      case 'SHM':
        return ['SHM_ADMIN'];
      case 'FBDE':
        return ['FBDE_ADMIN'];
      case 'HR':
        return ['HM'];
      case 'TPO':
        return ['FBDE', 'CONSULTANT', 'FREELANCE_COUNSELLOR','ADMIN'];
      default:
        return role;
    }
  };

const currentUser = useRbac()

  // Fetch pending unblock requests count
  const fetchPendingUnblockCount = async () => {
    if (currentUser.user?.role === 'ADMIN') {
      try {
        const response = await getUnblockRequestsForReview(1, 100, 'pending');
        if (response.success) {
          // If userId is provided, filter by that user
          let count = response.data.requests.length;
          if (user?.id) {
            count = response.data.requests.filter(
              req => req.requestedBy._id === user.id
            ).length;
          }
          setPendingUnblockCount(count);
        }
      } catch (error) {
        console.error('Failed to fetch pending unblock count:', error);
      }
    }
  };

  useEffect(() => {
    fetchPendingUnblockCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingUnblockCount, 30000);
    return () => clearInterval(interval);
  }, [currentUser.user?.role, user?.id]);

  useEffect(() => {
    if (user) {
      setEditData({
        username: user.username,
        location: user.location || '',
        phone: user.phone || '',
      });
      setSelectedRole(user.role || '');
    }
  }, [user]);

  const rbacUser = useRbac().user;

  useEffect(() => {
    if (!selectedRole) {
      setManagers([]);
      setIsLoadingManagers(false);
      return;
    }

    const apiRole = getApiRoleParam(selectedRole);
    if (apiRole === 'TPO') {
      setManagers([]);
      setIsLoadingManagers(false);
      return;
    }

    let cancelled = false;
    setIsLoadingManagers(true);

    const loadManagers = async () => {
      try {
        let results: Manager[] = [];
        if (Array.isArray(apiRole)) {
          const responses = await Promise.all(
            apiRole.map((role) => getAssignedManagers(role, managerSearch))
          );
          const combined = responses.flat();
          results = combined.filter(
            (manager, index, self) => index === self.findIndex((m) => m.id === manager.id)
          );
        } else {
          const response = await getAssignedManagers(apiRole, managerSearch);
          results = response?.length ? response : [];
        }
        if (!cancelled) {
          setManagers(results);
        }
      } catch (error: any) {
        console.error('Error fetching managers:', error);
        if (!cancelled) {
          toast.error('Failed to fetch managers. Please try again later.');
          setManagers([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingManagers(false);
        }
      }
    };

    const debounceMs = managerSearch.trim() ? 300 : 0;
    const timeoutId = setTimeout(loadManagers, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [selectedRole, managerSearch]);

  if (!user) return null;

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'HR': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'SHM': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'BDE': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'HM':
      case 'AGENCY': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBadgeColor = (isBlocked: boolean) => {
    return !isBlocked 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const formatDateTime = (value?: string | Date | null) => {
    if (!value) {
      return 'N/A';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Add these validation functions near the top of the component
  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const validatePassword = (password: string): boolean => {
    // At least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Validate phone input to only allow numbers
    if (name === 'phone') {
      if (value !== '' && !/^\d*$/.test(value)) {
        return; // Don't update state if input contains non-digits
      }
      if (value.length > 10) {
        return; // Don't update state if length exceeds 10
      }
    }

    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    // Validate phone number
    if (!validatePhoneNumber(editData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    try {
      const response = await updateUserDetails(user.id, editData);
      toast.success('User details updated successfully');
      setIsEditOpen(false);
      if (onUserUpdate && response.data.user) {
        onUserUpdate(response.data.user);
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error("This phone number is already associated with another account");
      } else {
        toast.error('Failed to update user details');
      }
    }
  };

  const handleRoleChange = async () => {
    if (!user || !selectedRole || !selectedManagerUserId) {
      toast('Select a role to see managers', { icon: 'ℹ️' });
      return;
    }
    setRoleChangeLoading(true);
  
    try {
      await changeUserRole(user.id, {
        newRole: selectedRole,
        newManagerUserId: selectedManagerUserId,
      });
      toast.success(`Role changed to ${selectedRole} for ${user.username}`);
      setIsRoleDialogOpen(false);
      setUserEdited(true);
    } catch (error) {
      console.error('Failed to change user role:', error);
      toast.error('Failed to change role');
    }
    setRoleChangeLoading(false);
    setSelectedRole('');
    setSelectedManagerUserId('');
  };

  const handlePasswordChange = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in both password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!validatePassword(newPassword)) {
      toast.error('Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }

    setPasswordChangeLoading(true);
    try {
      await changeUserPassword(user.id, newPassword, confirmPassword);
      toast.success('Password updated successfully');
      setIsPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to update password');
    }
    setPasswordChangeLoading(false);
  };

  const handleBlockToggle = async () => {
    if (!user) return;
    setBlockLoading(true);
    try {
      const result = await updateUserBlockStatus(user.id, !user.isBlocked);
      const responseData = result?.data;
      const nextStatus = responseData?.isBlocked ?? !user.isBlocked;
      toast.success(nextStatus ? 'User blocked' : 'User unblocked');
      if (onUserUpdate) {
        onUserUpdate({
          ...user,
          isBlocked: nextStatus,
          blockHistory: responseData?.blockHistory ?? user.blockHistory ?? [],
        });
      }
      setUserEdited(true);
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status');
    }
    setBlockLoading(false);
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={user.id}
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed top-0 right-0 h-full w-96 bg-white border-l border-border z-50 shadow-xl"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">User Details</h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="w-8 h-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* User Profile */}
            <div className="flex items-center gap-4">
             <div 
               className={cn(
                 "cursor-pointer transition-opacity hover:opacity-80",
                 !user.profilePhotoUrl && "cursor-default hover:opacity-100"
               )}
               onClick={() => user.profilePhotoUrl && setIsPhotoViewerOpen(true)}
             >
              <Avatar className="w-16 h-16">
                 {user.profilePhotoUrl && (
                   <AvatarImage src={user.profilePhotoUrl} alt={user.username} />
                 )}
                <AvatarFallback className="text-lg">
                  {user.username?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
             </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">{user.username}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getRoleBadgeColor(user.role)}>{user.role === "FREELANCE_COUNSELLOR" ? "Freelance Counsellor" : user.role}</Badge>
                  <Badge className={getStatusBadgeColor(user.isBlocked)}>
                    {user.isBlocked ? 'Blocked' : 'Active'}
                  </Badge>
                </div>
              </div>
            </div>

            <Separator />

            {/* Contact Information */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center justify-between">
                Contact Information
                {getApiRoleParam(user.role) === 'ADMIN' && (
                <Button variant="ghost" size="sm" onClick={() => setIsEditOpen(true)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                )}
              </h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.email}</p>
                    <p className="text-xs text-muted-foreground">Email Address</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.phone}</p>
                    <p className="text-xs text-muted-foreground">Phone Number</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.location || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">Location</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{new Date(user.createdAt).toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">Join Date</p>
                  </div>
                </div>
                {user.linkedinUrl && (
                  <div className="flex items-center gap-3">
                    <Linkedin className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <a 
                        href={user.linkedinUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                      >
                        View LinkedIn Profile
                      </a>
                      <p className="text-xs text-muted-foreground">LinkedIn</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Current Manager */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Current Manager</h4>
              {user.currentManager ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-foreground">{user.currentManager.username}</p>
                      <p className="text-xs text-muted-foreground">Manager Name</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-foreground">{user.currentManager.email}</p>
                      <p className="text-xs text-muted-foreground">Manager Email</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-foreground">{user.currentManager.phone}</p>
                      <p className="text-xs text-muted-foreground">Manager Phone</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No manager assigned</p>
              )}
            </div>

            <Separator />

            {/* Block Status History */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Block / Unblock History</h4>
              {user.blockHistory && user.blockHistory.length > 0 ? (
                <div className="space-y-3">
                  {[...user.blockHistory]
                    .sort(
                      (a, b) =>
                        new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
                    )
                    .map((entry, index) => (
                      <div
                        key={`${entry.changedAt}-${index}`}
                        className="flex items-start gap-3 rounded-md border border-border p-3"
                      >
                        <div
                          className={`mt-1 rounded-full p-2 ${
                            entry.isBlocked ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                          }`}
                        >
                          {entry.isBlocked ? (
                            <Shield className="w-4 h-4" />
                          ) : (
                            <ShieldOff className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-medium text-foreground">
                            {entry.isBlocked ? 'Blocked' : 'Unblocked'}
                            <span className="ml-2 text-xs text-muted-foreground">
                              {formatDateTime(entry.changedAt)}
                            </span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {entry.changedBy
                              ? `By ${entry.changedBy.username}${
                                  entry.changedBy.email ? ` • ${entry.changedBy.email}` : ''
                                }`
                              : 'Recorded automatically'}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No block or unblock activity recorded for this user yet.
                </p>
              )}
            </div>

            <Separator />

            {/* Role History */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Role History</h4>
              {user.roleHistory && user.roleHistory.length > 0 ? (
                <div className="space-y-6">
                  {[...user.roleHistory]
                    .sort((a, b) => {
                      if (a.endDate === null) return -1;
                      if (b.endDate === null) return 1;
                      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
                    })
                    .map((history, index) => (
                      <div
                        key={index}
                        className="relative flex items-start border-b border-gray-100 pb-4 last:border-b-0"
                      >
                        <div className="relative flex items-center justify-center w-6 h-6 mr-4">
                          <motion.div
                            className={`w-4 h-4 z-10 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors duration-300 ${
                              history.endDate === null
                                ? 'bg-[#ED795F] border-[#ED795F] text-white'
                                : 'bg-[#ED795F] border-[#ED795F] text-white p-[1.5px]'
                            }`}
                            initial={{ scale: 1, opacity: 1 }}
                            animate={{
                              scale: history.endDate === null ? [1, 1.1, 1] : 1,
                              opacity: history.endDate === null ? [1, 0.9, 1] : 1,
                            }}
                            transition={{
                              duration: 0.8,
                              repeat: history.endDate === null ? Infinity : 0,
                              ease: 'easeInOut',
                            }}
                          >
                            {history.endDate !== null && (
                              <svg
                                className="w-3 h-3"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </motion.div>
                          {history.endDate === null && (
                            <motion.div
                              className="absolute rounded-full border-2 border-[#ED795F] bg-transparent z-0"
                              style={{
                                width: '75%',
                                height: '75%',
                                top: '13%',
                                left: '13%',
                                transform: 'translate(-50%, -50%)',
                              }}
                              animate={{
                                scale: [1, 1.5, 1],
                                opacity: [0.5, 0.2, 0.5],
                              }}
                              transition={{
                                duration: 1,
                                repeat: Infinity,
                                ease: 'easeOut',
                              }}
                            />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className={`font-semibold text-sm ${
                                history.endDate === null ? 'text-gray-800' : 'text-gray-400'
                              }`}
                            >
                              {history.role === "FREELANCE_COUNSELLOR" ? "Freelance Counsellor" : history.role}
                            </span>
                          </div>
                          <div className="text-xs space-y-1">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Start Date:</span>
                              <span
                                className={`${
                                  history.endDate === null ? 'text-gray-600' : 'text-gray-400'
                                } font-medium`}
                              >
                                {new Date(history.startDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">End Date:</span>
                              <span
                                className={`${
                                  history.endDate === null ? 'text-gray-600' : 'text-gray-400'
                                } font-medium`}
                              >
                                {history.endDate ? (
                                  new Date(history.endDate).toLocaleDateString()
                                ) : (
                                  <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                                    Current
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Manager:</span>
                              <span
                                className={`${
                                  history.endDate === null ? 'text-gray-600' : 'text-gray-400'
                                } font-medium`}
                              >
                                {history.manager?.username || 'N/A'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Manager Email:</span>
                              <span
                                className={`${
                                  history.endDate === null ? 'text-gray-600' : 'text-gray-400'
                                } font-medium`}
                              >
                                {history.manager?.email || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No role history available for {user.username}.</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-border space-y-3">
            <div className="grid grid-cols-2 gap-2">
           <Button variant="outline" size="sm" onClick={() => setIsRoleDialogOpen(true)} disabled={rbacUser.role !== 'ADMIN'} >
                <UserCog className="w-4 h-4 mr-2" />
                Change Role
              </Button>
              
              <Button variant="outline" size="sm" onClick={() => setIsPasswordDialogOpen(true)}>
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button> 
              
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant={user.isBlocked ? 'default' : 'destructive'}
                  className="w-full"
                  size="sm"
                  disabled={blockLoading}
                >
                  {blockLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Processing...
                    </span>
                  ) : user.isBlocked ? (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Unblock User
                    </>
                  ) : (
                    <>
                      <ShieldOff className="w-4 h-4 mr-2" />
                      Block User
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{user.isBlocked ? 'Unblock User' : 'Block User'}</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to {user.isBlocked ? 'unblock' : 'block'}{' '}
                    <span className="font-semibold text-[#EC6E51]">{user.username}</span>?{' '}
                    {user.isBlocked
                      ? 'This will restore their access to the system.'
                      : 'This will prevent them from accessing the system.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBlockToggle}>
                    {user.isBlocked ? 'Unblock' : 'Block'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {currentUser.user?.role === 'ADMIN' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setShowUnblockRequestsModal(true);
                  fetchPendingUnblockCount(); // Refresh count when opening modal
                }}
                className="w-full relative bg-white hover:bg-orange-50 text-orange-600 border-2 border-orange-600"
              >
                Unblock Requests
                {pendingUnblockCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingUnblockCount > 99 ? '99+' : pendingUnblockCount}
                  </span>
                )}
              </Button>
            )}
          </div>

          {/* Change Role Dialog */}
          <AnimatePresence>
            {isRoleDialogOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
                onClick={() => setIsRoleDialogOpen(false)}
              >
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Change Role for <span className="text-[#EC6E51]">{user.username}</span>
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-foreground">Select Role</label>
                    <Select
                      onValueChange={(value: string) => {
                        setSelectedRole(value);
                        setManagerSearch('');
                        setSelectedManagerUserId('');
                        setManagers([]);
                        setIsLoadingManagers(true);
                      }}
                      value={selectedRole}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role} value={role}>
                            {ROLE_LABELS[role] ?? role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-foreground">Manager</label>
                    <div className="relative">
                      <Popover 
                        open={managerPopoverOpen} 
                        onOpenChange={(open) => {
                          setManagerPopoverOpen(open);
                          if (open) {
                            setManagerSearch(""); // Clear search when popover opens
                          }
                        }}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={managerPopoverOpen}
                            disabled={isLoadingManagers}
                            className="w-full justify-between pr-8"
                          >
                            {isLoadingManagers ? (
                              'Loading managers...'
                            ) : selectedManagerUserId ? (() => {
                              const selected = managers.find((m) => m.id === selectedManagerUserId);
                              if (!selected) return "Select Manager";
                              return (
                                <span className="flex items-center gap-2 min-w-0 truncate">
                                  <span className="truncate">{selected.username}</span>
                                  {selected.role && (
                                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded shrink-0">
                                      {selected.role}
                                    </span>
                                  )}
                                </span>
                              );
                            })() : "Select Manager"}
                            
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        
                        {/* Clear button as separate element positioned absolutely */}
                        {selectedManagerUserId && (
                          <button
                            type="button"
                            className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-sm z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedManagerUserId("");
                            }}
                            aria-label="Clear selection"
                          >
                            <ClearIcon className="h-4 w-4 opacity-50 hover:opacity-100" />
                          </button>
                        )}
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                          <Command>
                            <CommandInput 
                              placeholder="Search managers..." 
                              value={managerSearch}
                              onValueChange={setManagerSearch}
                              disabled={isLoadingManagers}
                            />
                            <CommandList>
                              <CommandEmpty>
                                {isLoadingManagers
                                  ? 'Loading managers...'
                                  : managerSearch.trim()
                                  ? `No managers found matching "${managerSearch}"`
                                  : 'No managers found for this role'}
                              </CommandEmpty>
                              {!isLoadingManagers && (
                              <CommandGroup>
                                {managers.map((m) => (
                                  <CommandItem
                                    key={m.id}
                                    value={`${m.username} ${m.role ?? ''}`}
                                    onSelect={() => {
                                      setSelectedManagerUserId(m.id);
                                      setManagerPopoverOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4 shrink-0",
                                        selectedManagerUserId === m.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <span className="flex items-center gap-2 min-w-0">
                                      <span className="truncate">{m.username}</span>
                                      {m.role && (
                                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded shrink-0">
                                          {m.role}
                                        </span>
                                      )}
                                    </span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsRoleDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleRoleChange} disabled={roleChangeLoading}>
                      {roleChangeLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Change Password Dialog */}
          <AnimatePresence>
            {isPasswordDialogOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
                onClick={() => setIsPasswordDialogOpen(false)}
              >
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Change Password for <span className="text-[#EC6E51]">{user.username}</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">New Password</label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          className="w-full p-2 border border-border rounded-md pr-10"
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeOff className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">Confirm Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={e => setConfirmPassword(e.target.value)}
                          className="w-full p-2 border border-border rounded-md pr-10"
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsPasswordDialogOpen(false);
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handlePasswordChange} disabled={passwordChangeLoading}>
                      {passwordChangeLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Edit Popup */}
          <AnimatePresence>
            {isEditOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
                onClick={() => setIsEditOpen(false)}
              >
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Edit User Details for <span className="text-[#EC6E51]">{user.username}</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">Username</label>
                      <input
                        type="text"
                        name="username"
                        value={editData.username}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">Location</label>
                      <input
                        type="text"
                        name="location"
                        value={editData.location}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">Phone</label>
                      <input
                        type="text"
                        name="phone"
                        value={editData.phone}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit}>Save</Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      
      {/* Unblock Requests Modal */}
      <UnblockRequestsModal
        isOpen={showUnblockRequestsModal}
        onClose={() => setShowUnblockRequestsModal(false)}
        userId={user?.id}
        onRequestUpdated={fetchPendingUnblockCount}
      />

      {/* Profile Photo Viewer Dialog */}
      <Dialog open={isPhotoViewerOpen} onOpenChange={setIsPhotoViewerOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{user?.username}'s Profile Photo</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4">
            {user?.profilePhotoUrl ? (
              <img 
                src={user.profilePhotoUrl} 
                alt={user.username}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            ) : (
              <div className="text-muted-foreground">No profile photo available</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AnimatePresence>
  );
};

export default UserDetailSidebar;

function updateUser(formData: any) {
  throw new Error('Function not implemented.');
}
